import { Component, HostListener, OnInit } from '@angular/core';
import { isTaskClosed } from '../tasks';
import { TaskService } from '../task.service';
import { ListMyTasksRequest, ListMyTasksResponse, Pagination, Task, TaskCurrentSprint, TaskFilter, TaskMutateMask, TaskState } from '../protogen/planning/service_pb';
import { MatTableDataSource } from '@angular/material/table';

/**
 * A component that lists all tasks on the Current sprint.
 */
@Component({
  selector: 'app-mytasks',
  templateUrl: './mytasks.component.html',
  styleUrls: ['./mytasks.component.scss']
})
export class MytasksComponent implements OnInit {
  dataLoaded: boolean = false;

  displayedColumns: string[] = ['done', 'title', 'options'];
  dataSource: MatTableDataSource<Task.AsObject> = new MatTableDataSource();

  archiveNameDialog?: any;

  constructor(private taskService: TaskService) {
    this.fetchTasks();
  }

  ngOnInit(): void {
    this.archiveNameDialog = document.getElementById('archiveNameDialog');
    this.archiveNameDialog.addEventListener('close', (evt: any) => {
      const sprintName = this.archiveNameDialog.returnValue;
      if (sprintName && sprintName.length > 2 && sprintName.length <= 64) {
        this.archiveCurrentList(sprintName);
      } else {
        alert('Sprint name invalid (must be 3-64 chars long).');
      }
    });
  }

  async archiveCurrentList(sprintName: string) {
    await this.taskService.archiveCurrentSprint(sprintName);
    await this.fetchTasks();
  }

  async deleteTask(task: Task.AsObject) {
    await this.taskService.deleteTask(task);
    await this.fetchTasks();
  }

  // TODO: Is this necessary? Does it eat into my QPS?
  @HostListener('window:focus', ['$event'])
  handleFocus() {
    this.fetchTasks();
  }

  isTaskClosed(task: Task.AsObject) {
    return isTaskClosed(task);
  }

  async removeFromCurrent(task: Task.AsObject) {
    const mask: TaskMutateMask = new TaskMutateMask().setIsCurrentSprint(true);
    task.isCurrentSprint = false;
    await this.taskService.editTask(task, mask.toObject());
    await this.fetchTasks();
  }

  async toggleClosed(task: Task.AsObject) {
    const futureState = isTaskClosed(task) ? TaskState.INACTIVE : TaskState.COMPLETE;
    task.taskState = futureState;
    const mask: TaskMutateMask = new TaskMutateMask().setTaskState(true);
    try {
      await this.taskService.editTask(task, mask.toObject());
      await this.fetchTasks();
    } catch (err) {
      console.error(err);
    }
  }

  private async fetchOnePageOfTasks(continuationToken: string|null = null): Promise<ListMyTasksResponse.AsObject> {
    const req = new ListMyTasksRequest().setFilter(
      new TaskFilter().setTaskCurrentSprint(
          TaskCurrentSprint.CURRENT_SPRINT));
    if (continuationToken) {
      req.setPagination(new Pagination().setContinuationToken(continuationToken));
    }
    return this.taskService.listMyTasks(req);
  }

  private async fetchTasks() {
    let tasksList: Task.AsObject[] = [];
    let continuationToken = undefined;
    let doneFetching = false;
    while (!doneFetching) {
      const pageResponse: ListMyTasksResponse.AsObject =
          await this.fetchOnePageOfTasks(continuationToken);
      tasksList = tasksList.concat(pageResponse.tasksList);
      if (pageResponse.continuationToken) {
        continuationToken = pageResponse.continuationToken;
      } else {
        doneFetching = true;
        this.dataLoaded = true;
      }
    }

    // Sort manually here.
    tasksList.sort((a: Task.AsObject, b: Task.AsObject) => {
      // Open tasks sort before closed tasks.
      if (isTaskClosed(a) || isTaskClosed(b)) {
        // Put the closed task at the bottom. If both tasks are closed, we don't care the order.
        return isTaskClosed(a) ? 1 : -1;
      }

      // Then sort by # of previous sprints the tasks were on.
      if (a.previousSprintsList.length !== b.previousSprintsList.length) {
        console.log(`${a.previousSprintsList.length}, ${b.previousSprintsList.length}`);
        return b.previousSprintsList.length - a.previousSprintsList.length;
      }

      // Otherwise, if the two tasks are equivalent in age, sort by ascending task id.
      return parseInt(a.id || '0') - parseInt(b.id || '0');
    });

    const numTasks = tasksList.length;
    const numClosedTasks = tasksList.filter(t => isTaskClosed(t)).length;
    document.title = `Plan ${numClosedTasks}/${numTasks}`;

    this.dataSource = new MatTableDataSource(tasksList);
  }
}
